<template>
  <form class="form-group" @submit.prevent="handleSubmit" ref="formRef">
    <label for="name">Nom de l'adresse *</label>
    <input
      v-model="localData.name"
      type="text"
      id="name"
      placeholder="Ex: Gare routière"
      required
    />

    <label for="street">Rue *</label>
    <input
      v-model="localData.street"
      type="text"
      id="street"
      placeholder="Ex: 15 Avenue des Champs"
      required
    />

    <div class="group-rows">
      <div class="group-columns">
    <label for="postalCode">Code postal *</label>
    <input
      v-model="localData.postalCode"
      type="text"
      id="postalCode"
      placeholder="Ex: 75008"
      required
    />
      </div>
      <div class="group-columns">

    <label for="cityId">Ville *</label>
    <select v-model="localData.cityId" id="cityId" required>
      <option value="">-- Sélectionner une ville --</option>
      <option v-for="city in cities" :key="city.id" :value="city.id">
        {{ city.name }}
      </option>
    </select>
      </div>
    </div>
    <label for="complement">Complément (optionnel)</label>
    <input
      v-model="localData.complement"
      type="text"
      id="complement"
      placeholder="Ex: Bâtiment A, Entrée 2"
    />

    <label for="thumbnailFile">📷 Photo du lieu</label>
    <input
      type="file"
      id="thumbnailFile"
      accept="image/*"
      @change="handleFileChange"
    />

    <!-- Prévisualisation : image existante OU nouvelle sélection -->
    <div v-if="thumbnailPreview || existingThumbnailUrl" class="preview">
      <img
        :src="thumbnailPreview || existingThumbnailUrl"
        alt="Aperçu"
      />
      <div class="preview-actions">
        <button
          v-if="thumbnailPreview"
          type="button"
          @click="removeFile"
          class="btn btn-secondary"
        >
          Supprimer la nouvelle photo
        </button>
        <span v-if="existingThumbnailUrl && !thumbnailPreview" class="preview-label">
          Photo actuelle
        </span>
        <span v-if="thumbnailPreview" class="preview-label">
          Nouvelle photo (remplacera l'ancienne)
        </span>
      </div>
    </div>

    <div v-if="error" class="error-message">{{ error }}</div>
  </form>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { config } from '@/config/env.js'

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({
      name: '',
      street: '',
      postalCode: '',
      cityId: '',
      complement: '',
      thumbnailFile: null  // ⚠️ AJOUTÉ : Nom du fichier existant en BDD
    })
  },
  cities: { type: Array, default: () => [] }
})

const emit = defineEmits(['submit', 'cancel'])

const formRef = ref(null)
const error = ref('')
const thumbnailPreview = ref(null)  // Preview du nouveau fichier sélectionné
const existingThumbnailUrl = ref(null)  // URL de l'image existante

const localData = ref({
  name: props.formData?.name || '',
  street: props.formData?.street || '',
  postalCode: props.formData?.postalCode || '',
  cityId: props.formData?.cityId || '',
  complement: props.formData?.complement || '',
  thumbnailFile: null
})

// Charger l'image existante si elle existe
onMounted(() => {
  if (props.formData?.thumbnailName) {
    // Construire l'URL vers l'image existante de façon sûre
    try {
      const baseUrl = new URL(config.apiUrl)  // Prend le schéma + domaine
      existingThumbnailUrl.value = `${baseUrl.origin}/uploads/thumbnails/${props.formData.thumbnailName}`
    } catch (e) {
      console.error('URL API invalide :', config.apiUrl, e)
      existingThumbnailUrl.value = `/uploads/thumbnails/${props.formData.thumbnailName}`  // fallback local
    }
  }
})

watch(() => props.formData, (newFormData) => {
  if (newFormData) {
    localData.value = {
      name: newFormData.name || '',
      street: newFormData.street || '',
      postalCode: newFormData.postalCode || '',
      cityId: newFormData.cityId || '',
      complement: newFormData.complement || '',
      thumbnailFile: null
    }

    // Mettre à jour l'URL de l'image existante
    if (newFormData.thumbnailName) {
      existingThumbnailUrl.value = `${config.apiUrl.replace('/api', '')}/uploads/thumbnails/${newFormData.thumbnailName}`
    } else {
      existingThumbnailUrl.value = null
    }

    // Réinitialiser la preview du nouveau fichier
    thumbnailPreview.value = null
  }
}, { deep: true })

function handleFileChange(event) {
  const file = event.target.files[0]

  if (!file) {
    localData.value.thumbnailFile = null
    thumbnailPreview.value = null
    return
  }

  // Validation : taille max 5MB
  if (file.size > 5 * 1024 * 1024) {
    error.value = 'Le fichier est trop volumineux (max 5MB)'
    event.target.value = ''
    return
  }

  // Validation : type image uniquement
  if (!file.type.startsWith('image/')) {
    error.value = 'Seules les images sont acceptées'
    event.target.value = ''
    return
  }

  // Stocker le fichier
  localData.value.thumbnailFile = file
  error.value = ''

  // Créer une prévisualisation du nouveau fichier
  const reader = new FileReader()
  reader.onload = (e) => {
    thumbnailPreview.value = e.target.result
  }
  reader.readAsDataURL(file)
}

// Supprimer le nouveau fichier sélectionné
function removeFile() {
  localData.value.thumbnailFile = null
  thumbnailPreview.value = null
  const fileInput = document.getElementById('thumbnailFile')
  if (fileInput) fileInput.value = ''
}

const handleSubmit = () => {
  error.value = ''

  if (!formRef.value?.checkValidity()) {
    formRef.value?.reportValidity()
    return
  }

  emit('submit', { ...localData.value })
}

defineExpose({ handleSubmit })
</script>
